import type { NoticeCornerRadius, NoticeTextLayer } from './types.js';

// =============================================================================
// Shared text/media style helpers so the Player canvas, the Admin editor (CSS), the
// thumbnail rasterizer and the template gallery agree on the same defaults.
// =============================================================================

/** Historical player line height; the editor/thumbnail used 1.15/1.2 before this field existed. */
export const DEFAULT_TEXT_LINE_HEIGHT = 1.2;

export function textLineHeight(layer: Pick<NoticeTextLayer, 'lineHeight'>): number {
  const value = layer.lineHeight;
  return typeof value === 'number' && Number.isFinite(value) && value > 0 ? value : DEFAULT_TEXT_LINE_HEIGHT;
}

/** Player canvas line breaking: authored line breaks first, then spaces; a word wider than the line breaks per
 *  character. Text is never clipped on screen, so the editor/AI draft use this to predict overflow. */
export function wrapNoticeText(text: string, maxWidth: number, measure: (value: string) => number): string[] {
  const lines: string[] = [];
  for (const paragraph of text.split(/\r\n|\r|\n/)) {
    let current = '';
    for (const word of paragraph.split(/\s+/).filter(Boolean)) {
      const next = current ? `${current} ${word}` : word;
      if (measure(next) <= maxWidth) { current = next; continue; }
      if (current) lines.push(current);
      current = '';
      // A word without spaces (e.g. Korean) may itself exceed the width.
      for (const char of word) {
        if (current && measure(current + char) > maxWidth) { lines.push(current); current = char; }
        else current += char;
      }
    }
    lines.push(current);
  }
  return lines;
}

/** Normalises `cornerRadius` to [topLeft, topRight, bottomRight, bottomLeft], scaled by `scale`
 *  and clamped so opposite corners never overlap (`width`/`height` in the same space as the result). */
export function cornerRadii(radius: NoticeCornerRadius | undefined, width: number, height: number, scale = 1): [number, number, number, number] {
  const raw: [number, number, number, number] = typeof radius === 'number' ? [radius, radius, radius, radius]
    : Array.isArray(radius) && radius.length === 4 ? [radius[0], radius[1], radius[2], radius[3]] : [0, 0, 0, 0];
  const max = Math.max(0, Math.min(width, height) / 2);
  return raw.map(value => Math.max(0, Math.min(max, (Number.isFinite(value) ? value : 0) * scale))) as [number, number, number, number];
}

export function hasCornerRadius(radius: NoticeCornerRadius | undefined): boolean {
  return cornerRadii(radius, Infinity, Infinity).some(value => value > 0);
}

/** CSS `border-radius` for the editor / SVG preview (values already in the rendered pixel space). */
export function cornerRadiusCss(radius: NoticeCornerRadius | undefined, width: number, height: number, scale = 1): string | undefined {
  const radii = cornerRadii(radius, width, height, scale);
  return radii.some(value => value > 0) ? radii.map(value => `${value}px`).join(' ') : undefined;
}

/** Adds a rounded-rect path (no fill) — `ctx.roundRect` when available, arcs otherwise (older WebViews). */
export function traceRoundedRect(ctx: CanvasRenderingContext2D, x: number, y: number, width: number, height: number, radii: readonly [number, number, number, number]): void {
  const [tl, tr, br, bl] = radii;
  ctx.beginPath();
  if (typeof (ctx as { roundRect?: unknown }).roundRect === 'function') { ctx.roundRect(x, y, width, height, [tl, tr, br, bl]); return; }
  ctx.moveTo(x + tl, y);
  ctx.lineTo(x + width - tr, y); ctx.arcTo(x + width, y, x + width, y + tr, tr);
  ctx.lineTo(x + width, y + height - br); ctx.arcTo(x + width, y + height, x + width - br, y + height, br);
  ctx.lineTo(x + bl, y + height); ctx.arcTo(x, y + height, x, y + height - bl, bl);
  ctx.lineTo(x, y + tl); ctx.arcTo(x, y, x + tl, y, tl);
  ctx.closePath();
}
